import React, { useState } from 'react'
import {
  MapIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  UserIcon
} from '@heroicons/react/24/solid'
import {
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon as QuestionIcon,
  ArrowRightCircleIcon,
  BuildingOfficeIcon,
  NewspaperIcon as OrdersIcon
} from '@heroicons/react/24/outline'

import Link from 'next/link'
import useTheme from 'lib/theme/useTheme'
import { useHeaderShow } from './HeaderShow/useHeaderShow'
import SubMenuItemsTrucks from './components/common/SubMenuItemsTrucks'
import SubMenuItemsCustomers from './components/common/SubMenuItemsCustomers'
import MobileMenu from './components/mobile/MobileMenu'
import SubMenuItem from './components/common/SubMenuItem'
import MainMobileMenu from './components/mobile/MainMobileMenu'
import { useScrollPosition } from './useScrollPosition'
import { useHeaderConfig } from './HeaderProvider/useHeaderConfig'
import ForkIcon from 'components/icons/ForkIcon'

type Props = {
  headerAction?: React.ReactNode
  hideNavigation: boolean
  highlightCartItems: boolean
}

const MobileHeader = ({
  headerAction,
  hideNavigation,
  highlightCartItems
}: Props) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false)
  const [isShowHeader, setIsShowHeader] = useState(true)
  const { setShow: setContextShow } = useHeaderShow()

  const [isOpenTrucksMenu, setIsOpenTrucksMenu] = useState(false)
  const [isOpenCustomersMenu, setIsOpenCustomersMenu] = useState(false)
  const [isOpenProfileMenu, setIsOpenProfileMenu] = useState(false)

  const { user } = useHeaderConfig()
  const { theme } = useTheme()
  const themedHeader = theme.page.themedBackground

  const subMenuIsOpen =
    isOpenProfileMenu || isOpenCustomersMenu || isOpenTrucksMenu

  const hideHeader = !isShowHeader && !subMenuIsOpen && !isOpenMenu

  useScrollPosition(
    ({ prevPos, currPos }) => {
      if (document.body.scrollHeight <= window.innerHeight) {
        setIsShowHeader(true)
        setContextShow(true)
      } else {
        const isShow =
          (currPos.y >= prevPos.y || currPos.y >= 0) &&
          window.innerHeight < document.body.scrollHeight + prevPos.y

        if (isShow !== isShowHeader) {
          setIsShowHeader(isShow)
        }
        setContextShow(!hideHeader)
      }
    },
    [isShowHeader, hideHeader]
  )
  const closeMenu = () => {
    setIsOpenMenu(false)
    setIsOpenTrucksMenu(false)
    setIsOpenCustomersMenu(false)
    setIsOpenProfileMenu(false)
  }
  return (
    <>
      <MainMobileMenu
        closeMenu={closeMenu}
        headerAction={headerAction}
        hideHeader={hideHeader}
        hideNavigation={hideNavigation}
        highlightCartItems={highlightCartItems}
        isOpenMenu={isOpenMenu}
        openMenu={() => setIsOpenMenu(true)}
      />

      {isOpenCustomersMenu && (
        <MobileMenu
          isOpen={isOpenCustomersMenu}
          onClose={() => setIsOpenCustomersMenu(false)}
        >
          <SubMenuItemsCustomers />
        </MobileMenu>
      )}

      {isOpenTrucksMenu && (
        <MobileMenu
          isOpen={isOpenTrucksMenu}
          onClose={() => setIsOpenTrucksMenu(false)}
        >
          <SubMenuItemsTrucks />
        </MobileMenu>
      )}

      {isOpenProfileMenu && (
        <MobileMenu
          isOpen={isOpenProfileMenu}
          onClose={() => setIsOpenProfileMenu(false)}
        >
          <>
            {user && user.amountCredit.cents > 0 && (
              <p className="text-orange-500 italic font-bold p-2 -mt-4">
                You have {user.amountCredit.formatted} credits
              </p>
            )}
            <SubMenuItem
              href="/profile"
              Icon={UserIcon}
              title="Account Information"
            />
            <SubMenuItem
              href="/profile/orders"
              Icon={OrdersIcon}
              title="My Orders"
            />
            <SubMenuItem
              href="/profile/caterings"
              Icon={OrdersIcon}
              title="My Bookings"
            />
            <SubMenuItem
              href="/profile/subscriptions"
              Icon={OrdersIcon}
              title="My Subscriptions"
            />
            {user?.managerOfClientCompany && (
              <SubMenuItem
                href={user.managerOfClientCompany.dashboardUrl}
                Icon={BuildingOfficeIcon}
                title="Corporate Dashboard"
                description={user.managerOfClientCompany.name}
              />
            )}
            <SubMenuItem
              href="/customers/logout"
              Icon={ArrowRightOnRectangleIcon}
              title="Logout"
            />
          </>
        </MobileMenu>
      )}

      {!subMenuIsOpen && (
        <MobileMenu isOpen={isOpenMenu}>
          <>
            {!hideNavigation && (
              <>
                <SubMenuItem
                  href="/food-trucks-map"
                  Icon={MapIcon}
                  title="Food trucks map"
                />
                <SubMenuItem
                  href="/food-trucks/catering"
                  Icon={ForkIcon}
                  title="Catering"
                />
                <SubMenuItem
                  Icon={ArrowRightCircleIcon}
                  onClick={() => setIsOpenCustomersMenu(true)}
                  title="For customers"
                  showChevron
                />

                <SubMenuItem
                  Icon={TruckIcon}
                  onClick={() => setIsOpenTrucksMenu(true)}
                  title="For truck owners"
                  showChevron
                />

                <SubMenuItem href="/faq" Icon={QuestionIcon} title="FAQ" />
                <SubMenuItem
                  href="/food-trucks-search"
                  Icon={MagnifyingGlassIcon}
                  title="Search"
                />
              </>
            )}

            {user ? (
              <SubMenuItem
                Icon={UserIcon}
                onClick={() => setIsOpenProfileMenu(true)}
                title="Profile"
                showChevron
              />
            ) : themedHeader ? (
              <Link
                className="w-full flex items-center justify-center px-4 py-2.5 text-white font-theme-body font-semibold rounded-theme-button transition bg-theme-button hover:bg-theme-button-hover"
                href="/customers/login"
                prefetch={false}
              >
                Login
              </Link>
            ) : (
              <Link
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-xs text-base font-medium text-white bg-orange-600 hover:bg-orange-700"
                href="/customers/login"
                prefetch={false}
              >
                Login
              </Link>
            )}
          </>
        </MobileMenu>
      )}
    </>
  )
}

export default MobileHeader
